import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Save, FileText, Settings2, Megaphone, ShieldCheck, Palette,
  Facebook, Twitter, Mail, Plus, X, PlaySquare, Calendar, Code2, Copy, ExternalLink, Link2,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="pr-4">
        <p className="text-sm text-white font-medium">{label}</p>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} className="data-[state=checked]:bg-amber-500" />
    </div>
  );
}

function SectionLabel({ children }) {
  return <p className="text-[10px] font-semibold text-gray-500 tracking-widest mt-6 mb-1 first:mt-0">{children}</p>;
}

function GeoFilterRow({ title, filter, onChange, testid }) {
  return (
    <div className="py-2">
      <p className="text-sm text-white font-medium mb-2">{title}</p>
      <Select value={filter.type} onValueChange={(v) => onChange({ ...filter, type: v })}>
        <SelectTrigger data-testid={testid} className="bg-gray-950 border-gray-800 focus:ring-amber-500/40 max-w-xs"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-card border-gray-800 text-white">
          <SelectItem value="any" className="focus:bg-gray-800">Any (No Restriction)</SelectItem>
          <SelectItem value="allow" className="focus:bg-gray-800">Allow only listed</SelectItem>
          <SelectItem value="block" className="focus:bg-gray-800">Block listed</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export default function PlayerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [embedResponsive, setEmbedResponsive] = useState(false);
  const [embedW, setEmbedW] = useState(640);
  const [embedH, setEmbedH] = useState(360);

  useEffect(() => {
    api.get(`/players/${id}`).then((r) => setP(r.data)).catch(() => {
      toast.error("Player not found"); navigate("/players");
    }).finally(() => setLoading(false));
  }, [id]);

  const setTop = (k, v) => setP((cur) => ({ ...cur, [k]: v }));
  const setNested = (group, k, v) => setP((cur) => ({ ...cur, [group]: { ...cur[group], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { name, description, active, social_sharing, settings, advertising, security, appearance } = p;
      const res = await api.put(`/players/${id}`, { name, description, active, social_sharing, settings, advertising, security, appearance });
      setP(res.data);
      toast.success("Player saved");
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const addDomain = () => {
    const d = domainInput.trim();
    if (!d) return;
    if (p.security.domains.includes(d)) { toast.error("Domain already added"); return; }
    setNested("security", "domains", [...p.security.domains, d]);
    setDomainInput("");
  };
  const removeDomain = (d) => setNested("security", "domains", p.security.domains.filter((x) => x !== d));

  if (loading || !p) return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;

  const s = p.settings, ap = p.appearance, sec = p.security, ad = p.advertising, ss = p.social_sharing;

  const copyText = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied!`); };
  const previewUrl = `${window.location.origin}/embed/player/${p.id}`;
  const embedCode = embedResponsive
    ? `<div style="position:relative;width:100%;padding-top:56.25%;">\n  <iframe src="${previewUrl}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>\n</div>`
    : `<iframe src="${previewUrl}" width="${embedW}" height="${embedH}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;

  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto">
      <button data-testid="back-to-players" onClick={() => navigate("/players")} className="flex items-center gap-2 text-sm text-gray-400 hover:text-amber-400 transition-colors mb-5">
        <ArrowLeft className="h-4 w-4" /> Back to Players
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-8">
        <div>
          <h1 data-testid="player-name-heading" className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
            <PlaySquare className="h-7 w-7 text-amber-400" /> {p.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
            <span className="text-gray-500 font-mono text-xs">ID: {p.id.slice(0, 12)}</span>
            <span className="text-gray-700">·</span>
            <span className={"inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border " + (p.active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-500/15 text-gray-400 border-gray-500/30")}>{p.active ? "ACTIVE" : "INACTIVE"}</span>
            <span className="text-gray-700">·</span>
            <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Calendar className="h-3.5 w-3.5" /> {fmtDate(p.created_at)}</span>
          </div>
        </div>
        <button data-testid="save-player-button" onClick={save} disabled={saving} className="gold-gradient text-black font-semibold flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm hover:brightness-110 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-60 w-max">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-transparent border-b border-gray-800 rounded-none w-full justify-start h-auto p-0 gap-6 overflow-x-auto">
          {[
            ["overview", "Overview", FileText],
            ["settings", "Settings", Settings2],
            ["advertising", "Advertising", Megaphone],
            ["security", "Security & Access", ShieldCheck],
            ["appearance", "Appearance", Palette],
            ["share", "Embed & Share", Code2],
          ].map(([v, label, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`ptab-${v}`} className="whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:text-amber-400 data-[state=active]:bg-transparent text-gray-400 px-1 pb-3 pt-0 font-medium flex items-center gap-2">
              <Icon className="h-4 w-4" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="pt-6">
          <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
            <div className="space-y-2">
              <Label className="text-gray-300">Name</Label>
              <Input data-testid="player-name-input" value={p.name} onChange={(e) => setTop("name", e.target.value)} className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Textarea data-testid="player-description-input" value={p.description} onChange={(e) => setTop("description", e.target.value)} rows={3} className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 resize-none" />
            </div>
            <div className="h-px bg-gray-800" />
            <ToggleRow testid="player-active-toggle" label="Active — player is enabled" checked={p.active} onChange={(v) => setTop("active", v)} />
            <div className="h-px bg-gray-800" />
            <SectionLabel>SOCIAL SHARING</SectionLabel>
            <ToggleRow testid="share-allow-toggle" label="Allow Sharing" hint="Enable social sharing options on the player" checked={ss.allow} onChange={(v) => setNested("social_sharing", "allow", v)} />
            <div className={"space-y-1 pl-1 " + (ss.allow ? "" : "opacity-40 pointer-events-none")}>
              <ToggleRow testid="share-facebook-toggle" label="Facebook" checked={ss.facebook} onChange={(v) => setNested("social_sharing", "facebook", v)} />
              <ToggleRow testid="share-twitter-toggle" label="X (Twitter)" checked={ss.twitter} onChange={(v) => setNested("social_sharing", "twitter", v)} />
              <ToggleRow testid="share-email-toggle" label="Email" checked={ss.email} onChange={(v) => setNested("social_sharing", "email", v)} />
            </div>
          </div>
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings" className="pt-6">
          <div className="bg-card border border-gray-800 rounded-2xl p-6 divide-y divide-gray-800/60">
            <div>
              <SectionLabel>PLAYBACK</SectionLabel>
              <ToggleRow testid="set-autoplay" label="Autoplay" checked={s.autoplay} onChange={(v) => setNested("settings", "autoplay", v)} />
              <ToggleRow testid="set-loop" label="Loop" checked={s.loop} onChange={(v) => setNested("settings", "loop", v)} />
              <ToggleRow testid="set-muted" label="Muted" hint="Start with audio muted" checked={s.muted} onChange={(v) => setNested("settings", "muted", v)} />
              <ToggleRow testid="set-seek" label="Allow Seek Forward" checked={s.allow_seek_forward} onChange={(v) => setNested("settings", "allow_seek_forward", v)} />
              <ToggleRow testid="set-resume" label="Resume Playback" hint="Remember playback position across sessions" checked={s.resume_playback} onChange={(v) => setNested("settings", "resume_playback", v)} />
              <ToggleRow testid="set-inline" label="Play Inline" hint="Play inline on mobile instead of fullscreen" checked={s.play_inline} onChange={(v) => setNested("settings", "play_inline", v)} />
            </div>
            <div className="pt-2">
              <SectionLabel>DISPLAY</SectionLabel>
              <ToggleRow testid="set-download" label="Allow Download" checked={s.allow_download} onChange={(v) => setNested("settings", "allow_download", v)} />
              <ToggleRow testid="set-title" label="Show Title" checked={s.show_title} onChange={(v) => setNested("settings", "show_title", v)} />
              <ToggleRow testid="set-captions" label="Show Captions" checked={s.show_captions} onChange={(v) => setNested("settings", "show_captions", v)} />
            </div>
            <div className="pt-2">
              <SectionLabel>PLAYER CONTROLS</SectionLabel>
              <ToggleRow testid="set-fullscreen" label="Allow Fullscreen" checked={s.allow_fullscreen} onChange={(v) => setNested("settings", "allow_fullscreen", v)} />
              <ToggleRow testid="set-pip" label="Allow Picture-in-Picture" checked={s.allow_pip} onChange={(v) => setNested("settings", "allow_pip", v)} />
              <ToggleRow testid="set-casting" label="Allow Casting" checked={s.allow_casting} onChange={(v) => setNested("settings", "allow_casting", v)} />
              <ToggleRow testid="set-speed" label="Allow Playback Speed" checked={s.allow_playback_speed} onChange={(v) => setNested("settings", "allow_playback_speed", v)} />
            </div>
          </div>
        </TabsContent>

        {/* ADVERTISING */}
        <TabsContent value="advertising" className="pt-6">
          <div className="bg-card border border-gray-800 rounded-2xl p-6">
            <SectionLabel>AD SOURCE</SectionLabel>
            <div className="space-y-2 max-w-sm mt-2">
              <Label className="text-gray-300">Source Type</Label>
              <Select value={ad.source_type} onValueChange={(v) => setNested("advertising", "source_type", v)}>
                <SelectTrigger data-testid="ad-source-type" className="bg-gray-950 border-gray-800 focus:ring-amber-500/40"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-gray-800 text-white">
                  <SelectItem value="none" className="focus:bg-gray-800">None (Ads Disabled)</SelectItem>
                  <SelectItem value="vast" className="focus:bg-gray-800">VAST Tag</SelectItem>
                  <SelectItem value="vmap" className="focus:bg-gray-800">VMAP Playlist</SelectItem>
                  <SelectItem value="google_ima" className="focus:bg-gray-800">Google IMA</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Choose how ads are served during playback.</p>
            </div>
          </div>
        </TabsContent>

        {/* SECURITY */}
        <TabsContent value="security" className="pt-6">
          <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
            <SectionLabel>ACCESS CONTROL</SectionLabel>
            <div className="space-y-2 max-w-sm">
              <Label className="text-gray-300">Access Control</Label>
              <Select value={sec.access_control} onValueChange={(v) => setNested("security", "access_control", v)}>
                <SelectTrigger data-testid="access-control" className="bg-gray-950 border-gray-800 focus:ring-amber-500/40"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-gray-800 text-white">
                  <SelectItem value="none" className="focus:bg-gray-800">None (Open Access)</SelectItem>
                  <SelectItem value="signed_urls" className="focus:bg-gray-800">Signed URLs</SelectItem>
                  <SelectItem value="token_auth" className="focus:bg-gray-800">Token Authentication</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="h-px bg-gray-800" />
            <div>
              <p className="text-sm text-white font-medium">Domain Restrictions</p>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Restrict playback to specific domains. Only listed domains will be allowed.</p>
              <div className="flex gap-2 max-w-md">
                <Input data-testid="domain-input" value={domainInput} onChange={(e) => setDomainInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDomain())} placeholder="example.com" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
                <button data-testid="add-domain-button" onClick={addDomain} className="h-10 w-10 rounded-lg gold-gradient text-black flex items-center justify-center shrink-0 cursor-pointer"><Plus className="h-4 w-4" /></button>
              </div>
              {sec.domains.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {sec.domains.map((d) => (
                    <span key={d} data-testid={`domain-chip-${d}`} className="inline-flex items-center gap-1.5 bg-gray-950 border border-gray-800 rounded-full pl-3 pr-1.5 py-1 text-xs text-gray-300">
                      {d}
                      <button onClick={() => removeDomain(d)} className="h-4 w-4 rounded-full hover:bg-red-500/20 hover:text-red-400 flex items-center justify-center"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="h-px bg-gray-800" />
            <div>
              <p className="text-sm text-white font-medium">Geo-Fencing</p>
              <p className="text-xs text-gray-500 mt-0.5">Restrict playback by geographic location.</p>
              <div className="grid sm:grid-cols-2 gap-x-8 mt-2">
                <GeoFilterRow testid="geo-countries" title="Countries" filter={sec.countries} onChange={(v) => setNested("security", "countries", v)} />
                <GeoFilterRow testid="geo-states" title="States / Regions" filter={sec.states} onChange={(v) => setNested("security", "states", v)} />
                <GeoFilterRow testid="geo-dmas" title="DMAs" filter={sec.dmas} onChange={(v) => setNested("security", "dmas", v)} />
                <GeoFilterRow testid="geo-zips" title="Zip Codes" filter={sec.zips} onChange={(v) => setNested("security", "zips", v)} />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* APPEARANCE */}
        <TabsContent value="appearance" className="pt-6">
          <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
            <SectionLabel>LOGO OVERLAY</SectionLabel>
            <div className="space-y-2 max-w-md">
              <Label className="text-gray-300">Logo Overlay URL</Label>
              <Input data-testid="logo-overlay-input" value={ap.logo_overlay_url} onChange={(e) => setNested("appearance", "logo_overlay_url", e.target.value)} placeholder="https://…/logo.png" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
              {ap.logo_overlay_url && <img src={ap.logo_overlay_url} alt="logo" className="mt-2 h-16 rounded-lg border border-gray-800 bg-gray-950 object-contain p-2" />}
            </div>
            <div className="h-px bg-gray-800" />
            <SectionLabel>PLAYLIST</SectionLabel>
            <ToggleRow testid="appearance-show-description" label="Show Description" checked={ap.show_description} onChange={(v) => setNested("appearance", "show_description", v)} />
            <ToggleRow testid="appearance-show-date" label="Show Date" checked={ap.show_date} onChange={(v) => setNested("appearance", "show_date", v)} />
            <div className="space-y-2 max-w-xs">
              <Label className="text-gray-300">Playlist Bar Label</Label>
              <Input data-testid="playlist-bar-label" maxLength={12} value={ap.playlist_bar_label} onChange={(e) => setNested("appearance", "playlist_bar_label", e.target.value)} placeholder="e.g. LIVE" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
            </div>
            <div className="flex gap-10">
              <div>
                <Label className="text-gray-300 text-sm">Bar Background</Label>
                <div className="mt-2 flex items-center gap-2">
                  <input data-testid="bar-background-color" type="color" value={ap.bar_background} onChange={(e) => setNested("appearance", "bar_background", e.target.value)} className="h-10 w-14 rounded-lg bg-transparent border border-gray-800 cursor-pointer" />
                  <span className="text-xs text-gray-500 font-mono">{ap.bar_background}</span>
                </div>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Bar Text Color</Label>
                <div className="mt-2 flex items-center gap-2">
                  <input data-testid="bar-text-color" type="color" value={ap.bar_text_color} onChange={(e) => setNested("appearance", "bar_text_color", e.target.value)} className="h-10 w-14 rounded-lg bg-transparent border border-gray-800 cursor-pointer" />
                  <span className="text-xs text-gray-500 font-mono">{ap.bar_text_color}</span>
                </div>
              </div>
            </div>
            <div className="rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-2" style={{ background: ap.bar_background, color: ap.bar_text_color }}>
              <PlaySquare className="h-4 w-4" /> {ap.playlist_bar_label || "Preview Bar"}
            </div>
          </div>
        </TabsContent>

        {/* SHARE / EMBED */}
        <TabsContent value="share" className="pt-6">
          <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-6">
            <div>
              <SectionLabel>EMBED CODE</SectionLabel>
              <p className="text-sm text-gray-400 mt-1 mb-3">Pick a size, then paste this snippet into any website.</p>

              <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="flex gap-1.5">
                  {[[426,240,"SD"],[640,360,"360p"],[854,480,"480p"],[1280,720,"720p"]].map(([w,h,lbl]) => {
                    const active = !embedResponsive && embedW === w && embedH === h;
                    return (
                      <button key={lbl} type="button" data-testid={`embed-size-${lbl}`} onClick={() => { setEmbedResponsive(false); setEmbedW(w); setEmbedH(h); }}
                        className={"px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors " + (active ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-gray-950 text-gray-400 border-gray-800 hover:text-white")}>
                        {lbl}
                      </button>
                    );
                  })}
                  <button type="button" data-testid="embed-size-responsive" onClick={() => setEmbedResponsive(true)}
                    className={"px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors " + (embedResponsive ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-gray-950 text-gray-400 border-gray-800 hover:text-white")}>
                    Responsive
                  </button>
                </div>
                {!embedResponsive && (
                  <div className="flex items-center gap-2">
                    <Input data-testid="embed-width" type="number" value={embedW} onChange={(e) => setEmbedW(Math.max(1, parseInt(e.target.value) || 0))} className="w-24 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-9" />
                    <span className="text-gray-600 text-sm">×</span>
                    <Input data-testid="embed-height" type="number" value={embedH} onChange={(e) => setEmbedH(Math.max(1, parseInt(e.target.value) || 0))} className="w-24 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-9" />
                    <span className="text-xs text-gray-500">px</span>
                  </div>
                )}
              </div>

              <div className="relative">
                <pre data-testid="embed-code-block" className="bg-gray-950 border border-gray-800 rounded-lg p-4 pr-14 text-[11px] leading-relaxed font-mono text-amber-200 overflow-x-auto whitespace-pre-wrap break-all">{embedCode}</pre>
                <button data-testid="copy-embed-code" onClick={() => copyText(embedCode, "Embed code")} className="absolute top-3 right-3 h-8 w-8 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 flex items-center justify-center transition-colors"><Copy className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="h-px bg-gray-800" />

            <div>
              <SectionLabel>SHAREABLE PREVIEW LINK</SectionLabel>
              <p className="text-sm text-gray-400 mt-1 mb-3">A direct link anyone can open to preview this player.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 flex items-center gap-2 bg-gray-950 rounded-lg px-3 py-2.5 border border-gray-800 min-w-0">
                  <Link2 className="h-4 w-4 text-amber-400 shrink-0" />
                  <code data-testid="preview-link-value" className="text-sm text-gray-300 truncate font-mono">{previewUrl}</code>
                </div>
                <div className="flex gap-2">
                  <button data-testid="copy-preview-link" onClick={() => copyText(previewUrl, "Preview link")} className="px-3 py-2.5 rounded-lg bg-gray-950 border border-gray-800 text-gray-300 hover:text-amber-400 hover:border-amber-500/40 flex items-center gap-2 text-sm transition-colors"><Copy className="h-4 w-4" /> Copy</button>
                  <a data-testid="open-preview-link" href={previewUrl} target="_blank" rel="noreferrer" className="px-3 py-2.5 rounded-lg gold-gradient text-black font-semibold flex items-center gap-2 text-sm cursor-pointer"><ExternalLink className="h-4 w-4" /> Open</a>
                </div>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden border border-gray-800 bg-black aspect-video max-w-xl">
              <iframe data-testid="embed-preview-iframe" src={previewUrl} title="player-preview" className="w-full h-full" />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
